import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import ApiService from '../api.service.abstract';
import {
  NewAccommodation,
  NewAccommodationRewired,
  PrestationLock,
  rewireNewAccommodation,
  UNVERIFIED_LOCK,
  unwireAccommodationRewired,
  WHCacheChildrenPolicy,
  WHCacheRoomCapacity,
} from '@pulpo/pulpo-models';
import { APP_CONFIG } from '@pulpo/app-config';
import { RouteReuseStrategy } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NewAccommodationService extends ApiService<NewAccommodation> {
  constructor(
    @Inject(APP_CONFIG) environment: any,
    protected http: HttpClient,
    routeReuse: RouteReuseStrategy
  ) {
    super(environment, http, 'accommodation/api/accommodations', routeReuse);
    if (environment.name == 'localDev') {
      this.url = 'http://localhost:8088';
    }
  }

  //TODO remove when backedn changed
  public getRewired(
    id: number | string
  ): Observable<HttpResponse<NewAccommodationRewired>> {
    return this.http
      .get<NewAccommodation>(`${this.url}/${this.endpoint}/${id}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const res = new HttpResponse<NewAccommodationRewired>({
            body: rewireNewAccommodation(response.body),
            headers: response.headers,
            status: response.status,
            statusText: response.statusText,
            url: response.url || undefined,
          });
          return res;
        })
      );
  }

  public updateRewired(
    id: number | string,
    data: NewAccommodationRewired,
    routesToClear: string[] | undefined = undefined
  ): Observable<HttpResponse<NewAccommodationRewired>> {
    if (routesToClear) {
      this.clearRouteReuseStrategy(routesToClear);
    }
    const newAccommodation = unwireAccommodationRewired(data);
    return this.http
      .put<NewAccommodation>(
        `${this.url}/${this.endpoint}/${id}`,
        newAccommodation,
        {
          observe: 'response',
        }
      )
      .pipe(
        map((response) => {
          const res = new HttpResponse<NewAccommodationRewired>({
            body: rewireNewAccommodation(response.body),
            headers: response.headers,
            status: response.status,
            statusText: response.statusText,
            url: response.url || undefined,
          });
          return res;
        })
      );
  }

  public createRewired(
    data: NewAccommodationRewired,
    routesToClear: string[] | undefined = undefined
  ): Observable<HttpResponse<NewAccommodationRewired>> {
    if (routesToClear) {
      this.clearRouteReuseStrategy(routesToClear);
    }
    const newAccommodation = unwireAccommodationRewired(data);
    return this.http
      .post<NewAccommodation>(
        `${this.url}/${this.endpoint}`,
        newAccommodation,
        {
          observe: 'response',
        }
      )
      .pipe(
        map((response) => {
          const res = new HttpResponse<NewAccommodationRewired>({
            body: rewireNewAccommodation(response.body),
            headers: response.headers,
            status: response.status,
            statusText: response.statusText,
            url: response.url || undefined,
          });
          return res;
        })
      );
  }

  /** The product locking the hotel once imported into a trip, null when it is editable. */
  findLock(id: string): Observable<PrestationLock | null> {
    return this.http
      .get<PrestationLock | null>(`${this.url}/${this.endpoint}/${id}/lock`)
      .pipe(catchError(() => of(UNVERIFIED_LOCK)));
  }

  getCombinations(combinationInput: {
    childrenPolicy: WHCacheChildrenPolicy;
    capacity: WHCacheRoomCapacity;
  }): Observable<
    HttpResponse<{ adults: number; children: number; infants: number }[]>
  > {
    return this.http.post<
      { adults: number; children: number; infants: number }[]
    >(`${this.url}/${this.endpoint}/generate-combinations`, combinationInput, {
      observe: 'response',
    });
  }

  /**
   * Export room prices as CSV for WebHotelier
   * @param cacheId The cache ID
   * @param propertyCode The property code (accommodation reference)
   * @param roomCode The room code (room category providerId)
   * @param rateId The rate ID
   * @returns Observable of Blob containing CSV data
   */
  exportRoomPrices(
    cacheId: string,
    propertyCode: string,
    roomCode: string,
    rateId: number
  ): Observable<Blob> {
    return this.http.get<Blob>(
      `${this.url}/${this.endpoint}/webhotelier/export-room-prices?cacheId=${cacheId}&propertyCode=${propertyCode}&roomCode=${roomCode}&rateId=${rateId}`,
      {
        responseType: 'blob' as 'json',
      }
    ) as unknown as Observable<Blob>;
  }
}
